import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "HR & workforce operations",
  title: "Leave & shift-cover agent",
  cardSummary:
    "Take leave and MC requests, check them against policy and balances, route for approval, and find qualified cover for the shift — end to end.",
  heroSubhead:
    "We design, build and deploy an AI agent that takes leave and medical-certificate requests where your staff already are — WhatsApp, chat or your HR app — checks each against policy, entitlement and balance, validates the MC, routes it to the right approver, and flags who's qualified and free to cover the shift, so a manager approves in seconds instead of chasing a spreadsheet.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Leave admin stops eating HR's week and — the expensive part — an approved absence no longer leaves a shift silently short: the agent surfaces qualified cover before the gap becomes a service failure.",
    complexityLabel: "Medium–High",
    complexityWhy:
      "Multi-channel intake, an MC document check (vision), a policy/entitlement engine, and shift-cover matching against skills, availability and labour rules across HRIS and rostering — but a human still approves, which contains the autonomy.",
    timeToLive: "~a few weeks (one site / business unit, shadow mode first)",
    bestFit: "Shift-based workforces — retail, F&B, healthcare, logistics, manufacturing — where an absence means a gap to fill",
  },
  outcomes: [
    "Shifts stay covered — approved leave triggers qualified cover suggestions before the gap costs you service or sales",
    "HR gets its week back — routine leave and MC intake handled end-to-end, not keyed by hand",
    "Policy applied consistently — entitlement, balances, blackout dates and MC rules checked the same way every time",
    "Faster approvals — managers approve from a ready summary with cover options, not a spreadsheet chase",
  ],
  impact: {
    manualLabel: "Processing a request by hand",
    manualValue: "~10–20 min (incl. finding cover)",
    agentLabel: "With the agent",
    agentValue: "~30–60 sec approve",
    saved:
      "≈ 10–18 minutes per request — hundreds of hours a month at high MC/leave volumes — and the bigger win is coverage: every shift the agent helps backfill in time is a service failure or an overtime scramble avoided.",
    note: "Indicative; the actual time and coverage depend on request volume, how well your rostering data is structured, and how many roles need certified or skilled cover.",
  },
  businessProblem:
    "In a shift-based business, staff call in sick or ask for leave through whatever channel is to hand — a WhatsApp to the manager, a form, a photo of a clinic MC. HR then keys each one into the system, reads the MC, and checks entitlement, balance, notice period and blackout dates by hand. Then the harder part: a manager has to work out who can actually cover the shift — who's trained for the till or the ward, who's available, who isn't already over their hours — usually in a spreadsheet or a WhatsApp group. At a large retailer or hospital that's hundreds of requests a day. It's slow, it's applied inconsistently, and when a same-day MC lands the cover often isn't found in time, so the shift simply runs short.",
  manualWorkflow: {
    steps: [
      "Employee submits leave or an MC through whatever channel they use",
      "HR keys it into the HR system and reads the MC",
      "Check entitlement, balance, notice period and blackout dates by hand",
      "Route it to the right manager for approval",
      "The manager works out who can cover — skills, availability, overtime",
      "Update the roster and confirm back to everyone",
    ],
    breaks:
      "Two slow parts collide — the policy and balance checking, and the detective work of finding qualified cover — and when a same-day MC lands, cover often isn't found in time, so the shift runs short.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as a routing workflow: each request is first classified by leave type — annual, medical, emergency, unpaid — because each has a different policy path, approver and urgency. From there it runs a short chain: validate against entitlement and balance, check the MC, propose qualified and available cover, and assemble an approval summary — stopping for a human at approval and whenever a rule genuinely can't be met.",
    steps: [
      "Take the request (WhatsApp, chat or HR app)",
      "Classify the leave type and urgency",
      "Check entitlement, balance, policy & the MC",
      "Propose qualified, available shift cover",
      "Assemble an approval-ready summary for the manager",
    ],
    inLabel: "A leave/MC request arrives",
    outLabel: "An approval-ready summary + cover options",
    loopAround: {
      index: 3,
      label:
        "Re-checks the balance and policy — and re-runs the cover search with a widened pool — when the first pass can't satisfy a rule, rather than approving a request that breaks entitlement or leaves a shift short.",
    },
    humanStep: "The manager approves (or declines) and confirms the cover",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "Employee message",
    inMessage:
      "Boss I'm not feeling well today, got MC from clinic 1 day. Cannot come for my 2pm shift sorry 🙏 [photo of MC attached]",
    outLabel: "What the agent prepares",
    outLines: [
      "Recognised: 1-day medical leave, today, for Aina (Store 12, 2–10pm shift)",
      "MC validated: clinic name, date and 1-day duration read from the photo; within policy",
      "Balance OK: 3 of 14 days sick leave used this year, notice waived for medical",
      "Cover found: Faiz and Mei-Ling are both cashier-trained and free today; Faiz is already at Store 12",
    ],
    outResult: "Approval-ready: approve leave + one-tap assign Faiz to the 2pm shift",
    note: "Manager approves in one tap · roster and payroll updated on approval · ~RM 0.10–0.25 per request in AI cost.",
  },
  workflowDetail: [
    {
      title: "Take the request where staff are",
      detail:
        "The agent receives the request on the channel your staff actually use — a WhatsApp message, your HR app, or a chat — reads it (including mixed Malay / English / Chinese and voice-note transcripts) and confirms back what it understood: who, what leave type, which dates, which shift.",
    },
    {
      title: "Classify the leave type and urgency",
      detail:
        "It routes the request by type — annual, medical, emergency same-day, or unpaid — because each has a different policy path, approver and urgency. A same-day medical absence jumps the cover search; a planned annual leave runs the notice and blackout checks.",
    },
    {
      title: "Check policy, balance and the MC",
      detail:
        "Against the employee's live entitlement it checks balance, notice period and blackout dates, and for medical leave it reads the certificate with vision — clinic, dates, duration — validating it against policy and flagging anything that doesn't add up (back-dated, altered, mismatched).",
    },
    {
      title: "Propose qualified, available cover",
      detail:
        "For the affected shift it searches the live roster for staff who are trained/certified for the role, available, and within their hours and overtime limits — proposing a small set of compliant cover options, ordered by fit (same site, fairness of rotation), or saying plainly when none qualify.",
    },
    {
      title: "Assemble the approval summary",
      detail:
        "It hands the manager a ready-to-action summary: the request, the policy result, the validated MC fields, and the cover options — so approving leave and assigning cover is one tap, not a spreadsheet session.",
    },
    {
      title: "Hand to a human",
      detail:
        "The manager approves or declines and confirms the cover. That approval is what writes the leave record and the roster change — nothing is booked automatically, and any policy breach or missing cover is surfaced, never silently allowed.",
    },
  ],
  howWeBuild:
    "It reads the MC and the request but only ever proposes — the manager's approval is what writes leave to the HR system and assigns the cover. It works from your live roster and entitlement data, so a cover suggestion is someone actually qualified and rostered-free at that moment, not a name off a stale list, and it applies the same policy every time — the same blackout dates, notice rules and balance checks. The medical certificate is treated as sensitive: only the fields policy needs (validity, dates, duration) are used, not the diagnosis, and every decision it surfaces traces back to the rule and the roster record it came from.",
  agentLoop: [
    "the request + the MC + your live roster, balances & policy",
    "classify, validate & search for cover (MCP tools)",
    "re-check policy and cover before it hands the manager a decision",
  ],
  integrations: [
    "HRIS / leave system (entitlements, balances, leave records)",
    "Rostering / workforce management (shifts, skills, availability)",
    "The channel staff use — WhatsApp, chat or your HR app",
  ],
  architecture: [
    "Pattern: a routing front-end on the Claude Agent SDK classifies each request by leave type and urgency, then a short per-type chain runs validate → check MC → propose cover → summarise; each tool is exposed via an in-process MCP server.",
    "Context: the employee's live entitlement and balance, the policy rules for that leave type, and the live roster (who is skilled and rostered-free) are loaded per request, so checks and cover suggestions reflect the current state, not an overnight export.",
    "Tools: read-only entitlement/balance, policy and roster lookups; a vision extractor for the MC; a cover-matching solver (skill + availability + labour-rule constraints); and one write tool that only drafts a leave record and a proposed roster change for a manager to confirm — no tool books leave or edits the roster on its own.",
    "Guardrails & gate: nothing is approved autonomously — the agent proposes and a manager's approval is the gate that writes leave and assigns cover; anything that breaks a rule (insufficient balance, notice period, no qualified cover) is surfaced as a flag, never silently allowed.",
    "MC & sensitive-data handling (PDPA/PDPC): the medical certificate is treated as sensitive — only the fields policy needs (validity, dates, duration) are extracted and retained, diagnosis and free text are not stored, and access is least-privilege and logged.",
    "Untrusted input: the request text and the MC image are treated as untrusted (a forged reference or crafted message can't push an approval through), and authenticity flags — mismatched clinic, altered dates — route to a human.",
    "Cover-matching constraints: the solver respects skills and certifications, availability, maximum-hours and overtime rules, and fairness of rotation, so a suggestion is compliant and reasonable, not just any free body.",
    "Observability & audit: every classification, policy check, MC field used and cover suggestion is logged and traceable to the rule and roster record — an audit trail for HR and for labour-law / PDPA scrutiny.",
    "Eval harness: a golden set of real historical requests — clean, edge (back-dated MC, zero balance, blackout date, no cover available) and adversarial (forged MC) — with deterministic policy checks plus an LLM-as-judge on the fuzzy calls, regression-tested before any change goes live.",
  ],
  models: [
    {
      task: "Understand the request across channels & languages",
      model: "Claude Sonnet",
      why: "Reads mixed Malay / English / Chinese WhatsApp messages and pulls out leave type, dates and intent reliably.",
    },
    {
      task: "Read & validate the MC (vision)",
      model: "Claude Sonnet",
      why: "Reliable vision on photographed clinic certificates — clinic, dates, duration — including poor phone photos.",
    },
    {
      task: "High-volume classification & balance checks",
      model: "Claude Haiku",
      why: "Cheapest and fastest for the many simple leave-type and entitlement yes/no checks.",
    },
    {
      task: "Resolve scarce cover or conflicting policies",
      model: "Claude Opus",
      why: "Invoked only when cover is scarce or policies conflict — a small fraction of requests.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per request",
        value: "~RM 0.10–0.25",
        note: "message + MC vision + policy checks + cover search + summary, at Claude Sonnet/Haiku token rates",
      },
      {
        label: "AI usage — at 3,000 requests/month",
        value: "~RM 260–650 / month",
        note: "lower with the Batch API for planned (non-same-day) leave processed off-peak",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting your HRIS, rostering system and staff channel is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to a single under-staffed shift or an overtime scramble avoided",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on request volume, how many are medical (needing vision), and how hard cover is to find. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  modelChoice: {
    approach: "Single model, tiered by task",
    rationale:
      "A request shares one context — the employee's entitlement, the policy, and the roster — so a single well-instrumented model runs the judgement, with cheaper Haiku on the many simple checks and Opus reserved only for scarce-cover or conflicting-policy cases. Reliable, cheap per request, and easy to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single leave request and its cover search.",
  },
  evaluation: [
    "Deterministic checks first — entitlement, balance, notice period, blackout dates, MC validity",
    "Self-verification — the agent re-checks policy and re-runs cover with a wider pool before handing over, and flags rather than force an approval",
    "Business KPIs — HR time per request, share of shifts covered in time, approval turnaround, policy-exception rate",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure your current process on a sample — time per request, how consistently two HR staff apply the same policy, and what share of absences get covered in time today. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reach the right leave and cover outcome?",
        metrics: [
          {
            name: "Recommendation vs a senior HR / duty manager",
            measures:
              "Its leave decision and cover suggestion agree with an expert's on held-out historical requests",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the check sound and complete?",
        metrics: [
          {
            name: "Required checks executed",
            measures: "Every policy check runs — entitlement, balance, notice, blackout, MC — nothing skipped",
            target: "100%",
          },
          {
            name: "No policy-breaking approvals proposed",
            measures: "It never recommends approving a request that breaks entitlement, notice or a labour rule",
            target: "0 tolerated",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "MC field extraction accuracy",
            measures: "Did it read the certificate's clinic, dates and duration correctly?",
            target: "≥ 98%",
          },
          {
            name: "Cover-suggestion validity",
            measures: "Proposed staff are actually qualified, available and within hours/overtime rules",
            target: "≥ 98%",
          },
          {
            name: "Coverage rate",
            measures: "Share of absences it finds valid cover for, measured against the human baseline",
            target: "≥ baseline",
          },
          {
            name: "No sensitive-data leakage",
            measures: "Only the policy-needed MC fields are retained; diagnosis and free text are never stored",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not write leave or a roster change on its own — a manager approves. It goes live per site only after it beats the human baseline and clears these GPA targets on a golden set of historical requests — including forged MCs, zero-balance and no-cover cases — running in shadow mode first. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical leave and MC requests — including the messy WhatsApp ones and the edge cases — running in shadow mode so you can see its decisions and cover suggestions before anything is booked. A focused single-site rollout is a matter of weeks.",
  ctaTitle: "Drowning in MC and leave requests?",
  ctaDescription:
    "Tell us your leave volume, HR system and how you roster, and we'll tell you honestly whether a leave & shift-cover agent is worth building — and how much cover it could find in time.",
  faqs: [
    {
      question: "Does the agent approve leave or change the roster by itself?",
      answer:
        "No. It reads, checks and proposes — a manager approves, and that approval is what writes the leave record and assigns the cover. It has no ability to book leave or edit the roster on its own; human-in-the-loop is the design, not an afterthought.",
    },
    {
      question: "How does it handle medical certificates and privacy?",
      answer:
        "It treats the MC as sensitive: it extracts only the fields policy needs — validity, dates, duration — and does not store the diagnosis or free text, PDPA/PDPC-aligned and least-privilege. A certificate that looks off (mismatched clinic, altered dates) is flagged to a human, not auto-accepted. Treat this as a starting point for your HR and compliance team, not legal advice.",
    },
    {
      question: "How does it find shift cover?",
      answer:
        "It matches the gap against your live roster on skills and certification, availability, maximum-hours and overtime rules, and fairness of rotation — proposing a few compliant options ranked by fit. If no one qualifies, it says so rather than suggest someone unsuitable.",
    },
    {
      question: "Will it work with WhatsApp and mixed languages?",
      answer:
        "Yes. It takes requests where your staff already are, understands mixed Malay, English and Chinese (and voice notes), and always confirms what it understood — who, what, which dates and shift — before anything is actioned.",
    },
    {
      question: "Does it work across Malaysia and Singapore?",
      answer:
        "Yes. It's configured per entity's leave policy and labour rules — Malaysia and Singapore differ — and it applies each site's ruleset. The same agent can serve both with the right policy set behind it.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept on one site or business unit is usually a matter of weeks, running in shadow mode first; a full rollout takes longer, mostly HRIS and rostering integration and getting managers comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "HR & operasi tenaga kerja",
  title: "Ejen cuti & ganti syif",
  cardSummary:
    "Terima permohonan cuti dan MC, semak terhadap polisi dan baki, halakan untuk kelulusan, dan cari ganti berkelayakan untuk syif — hujung ke hujung.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang menerima permohonan cuti dan sijil sakit (MC) di tempat kakitangan anda sudah berada — WhatsApp, sembang atau apl HR anda — menyemak setiap satu terhadap polisi, kelayakan dan baki, mengesahkan MC, menghalakannya kepada pelulus yang betul, dan menandakan siapa yang berkelayakan dan lapang untuk mengganti syif, supaya pengurus meluluskan dalam beberapa saat dan bukan mengejar hamparan.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Pentadbiran cuti berhenti membaham seminggu HR dan — bahagian mahal — ketidakhadiran yang diluluskan tidak lagi meninggalkan syif kekurangan secara senyap: ejen memaparkan ganti berkelayakan sebelum jurang menjadi kegagalan perkhidmatan.",
    complexityLabel: "Sederhana–Tinggi",
    complexityWhy:
      "Penerimaan pelbagai saluran, semakan dokumen MC (visi), enjin polisi/kelayakan, dan padanan ganti syif terhadap kemahiran, ketersediaan dan peraturan buruh merentas HRIS dan penjadualan — tetapi manusia tetap meluluskan, yang mengekang autonomi.",
    timeToLive: "~beberapa minggu (satu tapak / unit perniagaan, mod bayang dahulu)",
    bestFit: "Tenaga kerja berasaskan syif — runcit, F&B, penjagaan kesihatan, logistik, pembuatan — di mana ketidakhadiran bermakna jurang untuk diisi",
  },
  outcomes: [
    "Syif kekal berganti — cuti diluluskan mencetuskan cadangan ganti berkelayakan sebelum jurang merugikan perkhidmatan atau jualan",
    "HR mendapat semula minggunya — penerimaan cuti dan MC rutin diuruskan hujung ke hujung, bukan ditaip secara manual",
    "Polisi diterapkan secara konsisten — kelayakan, baki, tarikh sekatan dan peraturan MC disemak dengan cara sama setiap kali",
    "Kelulusan lebih pantas — pengurus meluluskan daripada ringkasan sedia dengan pilihan ganti, bukan mengejar hamparan",
  ],
  impact: {
    manualLabel: "Proses permohonan secara manual",
    manualValue: "~10–20 min (termasuk cari ganti)",
    agentLabel: "Dengan ejen",
    agentValue: "~30–60 saat lulus",
    saved:
      "≈ 10–18 minit setiap permohonan — beratus jam sebulan pada volum MC/cuti tinggi — dan kemenangan lebih besar ialah perlindungan: setiap syif yang ejen bantu ganti tepat masa ialah kegagalan perkhidmatan atau kelam-kabut kerja lebih masa yang dielakkan.",
    note: "Anggaran; masa dan perlindungan sebenar bergantung pada volum permohonan, sejauh mana tersusun data penjadualan anda, dan berapa banyak peranan memerlukan ganti bertauliah atau berkemahiran.",
  },
  businessProblem:
    "Dalam perniagaan berasaskan syif, kakitangan melaporkan sakit atau memohon cuti melalui apa saja saluran yang ada — WhatsApp kepada pengurus, borang, gambar MC klinik. HR kemudian menaip setiap satu ke dalam sistem, membaca MC, dan menyemak kelayakan, baki, tempoh notis dan tarikh sekatan secara manual. Kemudian bahagian lebih sukar: pengurus perlu memikirkan siapa yang benar-benar boleh mengganti syif — siapa terlatih untuk kaunter atau wad, siapa yang lapang, siapa yang belum melebihi jamnya — biasanya dalam hamparan atau kumpulan WhatsApp. Di peruncit besar atau hospital, itu beratus permohonan sehari. Ia perlahan, diterapkan secara tidak konsisten, dan apabila MC hari sama tiba, ganti sering tidak ditemui tepat masa, jadi syif itu hanya berjalan kekurangan.",
  manualWorkflow: {
    steps: [
      "Pekerja menghantar cuti atau MC melalui saluran yang mereka guna",
      "HR menaipnya ke sistem HR dan membaca MC",
      "Semak kelayakan, baki, tempoh notis dan tarikh sekatan secara manual",
      "Halakan kepada pengurus yang betul untuk kelulusan",
      "Pengurus memikirkan siapa boleh mengganti — kemahiran, ketersediaan, lebih masa",
      "Kemas kini jadual dan sahkan kembali kepada semua",
    ],
    breaks:
      "Dua bahagian perlahan berlanggar — semakan polisi dan baki, dan kerja siasat mencari ganti berkelayakan — dan apabila MC hari sama tiba, ganti sering tidak ditemui tepat masa, jadi syif berjalan kekurangan.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran routing: setiap permohonan mula-mula dikelaskan mengikut jenis cuti — tahunan, perubatan, kecemasan, tanpa gaji — kerana setiap satu ada laluan polisi, pelulus dan tahap kesegeraan berbeza. Dari situ ia menjalankan rantaian pendek: sahkan terhadap kelayakan dan baki, semak MC, cadangkan ganti berkelayakan dan lapang, dan himpunkan ringkasan kelulusan — berhenti untuk manusia pada kelulusan dan bila-bila peraturan benar-benar tidak dapat dipenuhi.",
    steps: [
      "Terima permohonan (WhatsApp, sembang atau apl HR)",
      "Kelaskan jenis cuti dan kesegeraan",
      "Semak kelayakan, baki, polisi & MC",
      "Cadangkan ganti syif berkelayakan dan lapang",
      "Himpunkan ringkasan sedia-kelulusan untuk pengurus",
    ],
    inLabel: "Permohonan cuti/MC tiba",
    outLabel: "Ringkasan sedia-kelulusan + pilihan ganti",
    loopAround: {
      index: 3,
      label:
        "Menyemak semula baki dan polisi — dan menjalankan semula carian ganti dengan kumpulan diperluas — apabila pas pertama tidak dapat memenuhi peraturan, dan bukan meluluskan permohonan yang melanggar kelayakan atau meninggalkan syif kekurangan.",
    },
    humanStep: "Pengurus meluluskan (atau menolak) dan mengesahkan ganti",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "Mesej pekerja",
    inMessage:
      "Boss saya tak sihat hari ni, ada MC dari klinik 1 hari. Tak boleh datang shift 2 petang, sorry 🙏 [gambar MC dilampirkan]",
    outLabel: "Apa yang ejen sediakan",
    outLines: [
      "Dikenali: cuti perubatan 1 hari, hari ini, untuk Aina (Kedai 12, syif 2–10 petang)",
      "MC disahkan: nama klinik, tarikh dan tempoh 1 hari dibaca dari gambar; dalam polisi",
      "Baki OK: 3 daripada 14 hari cuti sakit digunakan tahun ini, notis diketepikan untuk perubatan",
      "Ganti ditemui: Faiz dan Mei-Ling kedua-duanya terlatih juruwang dan lapang hari ini; Faiz sudah di Kedai 12",
    ],
    outResult: "Sedia-kelulusan: luluskan cuti + tugaskan Faiz ke syif 2 petang dengan satu ketik",
    note: "Pengurus meluluskan dengan satu ketik · jadual dan gaji dikemas kini semasa kelulusan · ~RM 0.10–0.25 setiap permohonan dalam kos AI.",
  },
  workflowDetail: [
    {
      title: "Terima permohonan di tempat kakitangan berada",
      detail:
        "Ejen menerima permohonan pada saluran yang kakitangan anda benar-benar guna — mesej WhatsApp, apl HR anda, atau sembang — membacanya (termasuk campuran Melayu / Inggeris / Cina dan transkrip nota suara) dan mengesahkan kembali apa yang difahaminya: siapa, jenis cuti apa, tarikh mana, syif mana.",
    },
    {
      title: "Kelaskan jenis cuti dan kesegeraan",
      detail:
        "Ia menghalakan permohonan mengikut jenis — tahunan, perubatan, kecemasan hari sama, atau tanpa gaji — kerana setiap satu ada laluan polisi, pelulus dan kesegeraan berbeza. Ketidakhadiran perubatan hari sama melompat ke carian ganti; cuti tahunan terancang menjalankan semakan notis dan sekatan.",
    },
    {
      title: "Semak polisi, baki dan MC",
      detail:
        "Terhadap kelayakan langsung pekerja ia menyemak baki, tempoh notis dan tarikh sekatan, dan untuk cuti perubatan ia membaca sijil dengan visi — klinik, tarikh, tempoh — mengesahkannya terhadap polisi dan menandakan apa-apa yang tidak masuk akal (bertarikh mundur, diubah, tidak sepadan).",
    },
    {
      title: "Cadangkan ganti berkelayakan dan lapang",
      detail:
        "Untuk syif terjejas ia mencari jadual langsung untuk kakitangan yang terlatih/bertauliah untuk peranan itu, lapang, dan dalam had jam dan lebih masa mereka — mencadangkan set kecil pilihan ganti yang patuh, disusun mengikut kesesuaian (tapak sama, keadilan giliran), atau menyatakan dengan jelas apabila tiada yang layak.",
    },
    {
      title: "Himpunkan ringkasan kelulusan",
      detail:
        "Ia menyerahkan pengurus ringkasan sedia-tindakan: permohonan, keputusan polisi, medan MC disahkan, dan pilihan ganti — jadi meluluskan cuti dan menugaskan ganti ialah satu ketik, bukan sesi hamparan.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Pengurus meluluskan atau menolak dan mengesahkan ganti. Kelulusan itulah yang menulis rekod cuti dan perubahan jadual — tiada apa ditempah automatik, dan sebarang pelanggaran polisi atau ganti hilang dipaparkan, tidak pernah dibenarkan secara senyap.",
    },
  ],
  howWeBuild:
    "Ia membaca MC dan permohonan tetapi hanya mencadang — kelulusan pengurus yang menulis cuti ke sistem HR dan menugaskan ganti. Ia bekerja daripada data jadual dan kelayakan langsung anda, jadi cadangan ganti ialah seseorang yang benar-benar berkelayakan dan lapang pada masa itu, bukan nama daripada senarai lapuk, dan ia menerapkan polisi sama setiap kali — tarikh sekatan, peraturan notis dan semakan baki yang sama. Sijil sakit dianggap sensitif: hanya medan yang polisi perlukan (kesahihan, tarikh, tempoh) digunakan, bukan diagnosis, dan setiap keputusan yang dipaparkan dijejak kembali ke peraturan dan rekod jadual asalnya.",
  agentLoop: [
    "permohonan + MC + jadual, baki & polisi langsung anda",
    "kelas, sahkan & cari ganti (alat MCP)",
    "semak semula polisi dan ganti sebelum ia menyerahkan pengurus satu keputusan",
  ],
  integrations: [
    "HRIS / sistem cuti (kelayakan, baki, rekod cuti)",
    "Penjadualan / pengurusan tenaga kerja (syif, kemahiran, ketersediaan)",
    "Saluran yang kakitangan guna — WhatsApp, sembang atau apl HR anda",
  ],
  architecture: [
    "Corak: bahagian hadapan routing atas Claude Agent SDK mengelaskan setiap permohonan mengikut jenis cuti dan kesegeraan, kemudian rantaian pendek per-jenis menjalankan sahkan → semak MC → cadang ganti → ringkas; setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: kelayakan dan baki langsung pekerja, peraturan polisi untuk jenis cuti itu, dan jadual langsung (siapa berkemahiran dan lapang) dimuatkan per permohonan, jadi semakan dan cadangan ganti mencerminkan keadaan semasa, bukan eksport semalaman.",
    "Alat: semakan kelayakan/baki, polisi dan jadual baca-sahaja; pengekstrak visi untuk MC; penyelesai padanan-ganti (kekangan kemahiran + ketersediaan + peraturan buruh); dan satu alat tulis yang hanya merangka rekod cuti dan perubahan jadual dicadang untuk pengurus sahkan — tiada alat menempah cuti atau mengedit jadual sendiri.",
    "Pagar keselamatan & get: tiada apa diluluskan secara autonomi — ejen mencadang dan kelulusan pengurus ialah get yang menulis cuti dan menugaskan ganti; apa-apa yang melanggar peraturan (baki tidak mencukupi, tempoh notis, tiada ganti berkelayakan) dipaparkan sebagai tanda, tidak pernah dibenarkan secara senyap.",
    "Pengendalian MC & data sensitif (PDPA/PDPC): sijil sakit dianggap sensitif — hanya medan yang polisi perlukan (kesahihan, tarikh, tempoh) diekstrak dan disimpan, diagnosis dan teks bebas tidak disimpan, dan akses adalah keistimewaan-minimum dan dilog.",
    "Input tidak dipercayai: teks permohonan dan imej MC dianggap tidak dipercayai (rujukan palsu atau mesej direka tidak boleh menolak kelulusan), dan tanda kesahihan — klinik tidak sepadan, tarikh diubah — dihalakan kepada manusia.",
    "Kekangan padanan-ganti: penyelesai menghormati kemahiran dan tauliah, ketersediaan, had jam maksimum dan peraturan lebih masa, dan keadilan giliran, jadi cadangan adalah patuh dan munasabah, bukan sekadar sesiapa yang lapang.",
    "Kebolehlihatan & audit: setiap pengelasan, semakan polisi, medan MC digunakan dan cadangan ganti dilog dan boleh dijejak ke peraturan dan rekod jadual — jejak audit untuk HR dan untuk penelitian undang-undang buruh / PDPA.",
    "Rangka penilaian: set emas permohonan sejarah sebenar — bersih, tepi (MC bertarikh mundur, baki sifar, tarikh sekatan, tiada ganti tersedia) dan musuh (MC palsu) — dengan semakan polisi deterministik serta LLM-sebagai-hakim untuk kes kabur, diuji regresi sebelum apa-apa perubahan beroperasi.",
  ],
  models: [
    {
      task: "Fahami permohonan merentas saluran & bahasa",
      model: "Claude Sonnet",
      why: "Membaca mesej WhatsApp campuran Melayu / Inggeris / Cina dan mengeluarkan jenis cuti, tarikh dan niat dengan boleh dipercayai.",
    },
    {
      task: "Baca & sahkan MC (visi)",
      model: "Claude Sonnet",
      why: "Visi boleh dipercayai pada sijil klinik difoto — klinik, tarikh, tempoh — termasuk gambar telefon yang kurang baik.",
    },
    {
      task: "Pengelasan & semakan baki bervolum tinggi",
      model: "Claude Haiku",
      why: "Paling murah dan pantas untuk banyak semakan ya/tidak jenis-cuti dan kelayakan yang mudah.",
    },
    {
      task: "Selesaikan ganti terhad atau polisi bercanggah",
      model: "Claude Opus",
      why: "Digunakan hanya apabila ganti terhad atau polisi bercanggah — sebahagian kecil permohonan.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap permohonan",
        value: "~RM 0.10–0.25",
        note: "mesej + visi MC + semakan polisi + carian ganti + ringkasan, pada kadar token Claude Sonnet/Haiku",
      },
      {
        label: "Penggunaan AI — pada 3,000 permohonan/bulan",
        value: "~RM 260–650 / bulan",
        note: "lebih rendah dengan Batch API untuk cuti terancang (bukan hari sama) diproses luar waktu puncak",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung HRIS, sistem penjadualan dan saluran kakitangan anda adalah beberapa minggu; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding satu syif kekurangan kakitangan atau kelam-kabut lebih masa yang dielakkan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada volum permohonan, berapa banyak ialah perubatan (memerlukan visi), dan sejauh mana sukar ganti ditemui. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  modelChoice: {
    approach: "Model tunggal, berperingkat mengikut tugas",
    rationale:
      "Satu permohonan berkongsi satu konteks — kelayakan pekerja, polisi, dan jadual — jadi model tunggal yang lengkap menjalankan pertimbangan, dengan Haiku lebih murah pada banyak semakan mudah dan Opus disimpan hanya untuk kes ganti terhad atau polisi bercanggah. Boleh dipercayai, murah setiap permohonan, dan mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk satu permohonan cuti dan carian gantinya.",
  },
  evaluation: [
    "Semakan deterministik dahulu — kelayakan, baki, tempoh notis, tarikh sekatan, kesahihan MC",
    "Pengesahan sendiri — ejen menyemak semula polisi dan menjalankan semula ganti dengan kumpulan lebih luas sebelum menyerahkan, dan menanda dan bukan memaksa kelulusan",
    "KPI perniagaan — masa HR setiap permohonan, peratus syif berganti tepat masa, pusing ganti kelulusan, kadar pengecualian polisi",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur proses semasa anda pada sampel — masa setiap permohonan, sejauh mana konsisten dua kakitangan HR menerapkan polisi sama, dan berapa peratus ketidakhadiran berganti tepat masa hari ini. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mencapai hasil cuti dan ganti yang betul?",
        metrics: [
          {
            name: "Syor vs pengurus HR / bertugas kanan",
            measures:
              "Keputusan cutinya dan cadangan ganti bersetuju dengan pakar pada permohonan sejarah terpencil",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah semakan kukuh dan lengkap?",
        metrics: [
          {
            name: "Semakan diperlukan dilaksanakan",
            measures: "Setiap semakan polisi berjalan — kelayakan, baki, notis, sekatan, MC — tiada dilangkau",
            target: "100%",
          },
          {
            name: "Tiada kelulusan melanggar-polisi dicadang",
            measures: "Ia tidak pernah mengesyorkan meluluskan permohonan yang melanggar kelayakan, notis atau peraturan buruh",
            target: "0 dibenarkan",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan ekstrak medan MC",
            measures: "Adakah ia membaca klinik, tarikh dan tempoh sijil dengan betul?",
            target: "≥ 98%",
          },
          {
            name: "Kesahihan cadangan ganti",
            measures: "Kakitangan dicadang benar-benar berkelayakan, lapang dan dalam peraturan jam/lebih masa",
            target: "≥ 98%",
          },
          {
            name: "Kadar perlindungan",
            measures: "Peratus ketidakhadiran yang ia temui ganti sah, diukur terhadap garis dasar manusia",
            target: "≥ garis dasar",
          },
          {
            name: "Tiada kebocoran data sensitif",
            measures: "Hanya medan MC yang polisi perlukan disimpan; diagnosis dan teks bebas tidak pernah disimpan",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menulis cuti atau perubahan jadual sendiri — pengurus meluluskan. Ia beroperasi per tapak hanya selepas ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas permohonan sejarah — termasuk MC palsu, baki sifar dan kes tiada ganti — berjalan dalam mod bayang dahulu. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas permohonan cuti dan MC sejarah sebenar anda — termasuk yang berselerak di WhatsApp dan kes tepi — berjalan dalam mod bayang supaya anda boleh melihat keputusan dan cadangan gantinya sebelum apa-apa ditempah. Pelancaran satu tapak yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Lemas dengan permohonan MC dan cuti?",
  ctaDescription:
    "Beritahu kami volum cuti, sistem HR dan cara anda menjadual, dan kami akan beritahu dengan jujur sama ada ejen cuti & ganti syif berbaloi dibina — dan berapa banyak ganti ia boleh temui tepat masa.",
  faqs: [
    {
      question: "Adakah ejen meluluskan cuti atau menukar jadual sendiri?",
      answer:
        "Tidak. Ia membaca, menyemak dan mencadang — pengurus meluluskan, dan kelulusan itulah yang menulis rekod cuti dan menugaskan ganti. Ia tiada keupayaan untuk menempah cuti atau mengedit jadual sendiri; manusia-dalam-gelung ialah reka bentuk, bukan renungan kemudian.",
    },
    {
      question: "Bagaimana ia mengendalikan sijil sakit dan privasi?",
      answer:
        "Ia menganggap MC sebagai sensitif: ia mengekstrak hanya medan yang polisi perlukan — kesahihan, tarikh, tempoh — dan tidak menyimpan diagnosis atau teks bebas, selaras PDPA/PDPC dan keistimewaan-minimum. Sijil yang kelihatan pelik (klinik tidak sepadan, tarikh diubah) ditandakan kepada manusia, bukan diterima automatik. Anggap ini titik permulaan untuk pasukan HR dan pematuhan anda, bukan nasihat undang-undang.",
    },
    {
      question: "Bagaimana ia mencari ganti syif?",
      answer:
        "Ia memadankan jurang terhadap jadual langsung anda pada kemahiran dan tauliah, ketersediaan, had jam maksimum dan peraturan lebih masa, dan keadilan giliran — mencadangkan beberapa pilihan patuh disusun mengikut kesesuaian. Jika tiada yang layak, ia menyatakannya dan bukan mencadangkan seseorang yang tidak sesuai.",
    },
    {
      question: "Adakah ia berfungsi dengan WhatsApp dan bahasa campuran?",
      answer:
        "Ya. Ia menerima permohonan di tempat kakitangan anda sudah berada, memahami campuran Melayu, Inggeris dan Cina (dan nota suara), dan sentiasa mengesahkan apa yang difahaminya — siapa, apa, tarikh dan syif mana — sebelum apa-apa ditindak.",
    },
    {
      question: "Adakah ia berfungsi merentas Malaysia dan Singapura?",
      answer:
        "Ya. Ia dikonfigurasi mengikut polisi cuti dan peraturan buruh setiap entiti — Malaysia dan Singapura berbeza — dan ia menerapkan set peraturan setiap tapak. Ejen yang sama boleh berkhidmat untuk kedua-duanya dengan set polisi yang betul di belakangnya.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep untuk satu tapak atau unit perniagaan biasanya beberapa minggu, berjalan dalam mod bayang dahulu; pelancaran penuh mengambil masa lebih lama, kebanyakannya integrasi HRIS dan penjadualan dan membiasakan pengurus, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "HR 与人力运营",
  title: "请假与补班智能体",
  cardSummary:
    "接收请假与病假单申请，对照政策与余额核查、转交审批，并为该班次找到合格顶班人选——端到端。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，在员工本来就在的地方接收请假与病假单（MC）申请——WhatsApp、聊天或你的 HR 应用——对照政策、额度与余额逐一核查、验证病假单、转交正确的审批人，并标出谁有资格且有空来补这个班次，让主管在几秒内完成审批，而不是去翻表格。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "请假事务不再吞掉 HR 的一周，而且——最花钱的部分——获批的缺勤不再让某个班次悄悄缺人：智能体在缺口酿成服务事故之前就给出合格的顶班人选。",
    complexityLabel: "中等–偏高",
    complexityWhy:
      "多渠道接收、病假单文档核查（视觉）、政策/额度引擎，以及跨 HRIS 与排班系统、按技能、可用性与劳动规则做的补班匹配——但仍由人审批，从而把自主性控制住。",
    timeToLive: "约数周（单一门店 / 业务单元，先影子模式）",
    bestFit: "轮班制的劳动力——零售、餐饮、医疗、物流、制造——缺勤即意味着需要填补的缺口",
  },
  outcomes: [
    "班次始终有人——获批的请假会在缺口影响服务或销售之前触发合格顶班建议",
    "HR 拿回一周时间——常规请假与病假单接收端到端处理，而非人工录入",
    "政策一致执行——额度、余额、封锁日期与病假单规则每次都以同样方式核查",
    "审批更快——主管从带顶班选项的现成摘要上审批，而不是翻表格",
  ],
  impact: {
    manualLabel: "人工处理一份申请",
    manualValue: "约 10–20 分钟（含找顶班）",
    agentLabel: "使用智能体",
    agentValue: "约 30–60 秒审批",
    saved:
      "每份申请约省 10–18 分钟——在高病假/请假量下每月数百小时——而更大的收益是覆盖：智能体帮助及时补上的每一个班次，都是一次避免的服务事故或加班救火。",
    note: "为估算；实际时间与覆盖取决于申请量、你的排班数据结构化程度，以及有多少岗位需要持证或有技能的顶班。",
  },
  businessProblem:
    "在轮班制的业务里，员工通过任何顺手的渠道报病假或请假——给主管发条 WhatsApp、一张表单、一张诊所病假单的照片。HR 随后把每一份录入系统、读病假单，并人工核查额度、余额、通知期与封锁日期。接着是更难的部分：主管得弄清谁真能补这个班——谁受过收银或病房的培训、谁有空、谁还没超工时——通常是在表格或 WhatsApp 群里。在大型零售商或医院，那是每天数百份申请。它缓慢、执行不一致，而当一张当天病假单到来时，顶班往往来不及找到，于是这个班次就只能缺人运转。",
  manualWorkflow: {
    steps: [
      "员工通过他们使用的任意渠道提交请假或病假单",
      "HR 把它录入 HR 系统并读取病假单",
      "人工核查额度、余额、通知期与封锁日期",
      "转交给正确的主管审批",
      "主管弄清谁能顶班——技能、可用性、加班",
      "更新排班并向各方确认",
    ],
    breaks:
      "两个缓慢环节相撞——政策与余额核查，以及寻找合格顶班的侦探活——而当当天病假单到来时，顶班往往来不及找到，于是班次缺人运转。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 routing（路由）方式构建：每份申请先按请假类型分类——年假、病假、紧急、无薪——因为各自有不同的政策路径、审批人与紧急程度。随后走一条简短链路：对照额度与余额核查、验证病假单、给出合格且有空的顶班建议、并组装审批摘要——在审批处以及任何规则确实无法满足时停下交由人处理。",
    steps: [
      "接收申请（WhatsApp、聊天或 HR 应用）",
      "对请假类型与紧急程度分类",
      "核查额度、余额、政策与病假单",
      "给出合格且有空的顶班建议",
      "为主管组装可审批的摘要",
    ],
    inLabel: "一份请假/病假单申请到达",
    outLabel: "一份可审批摘要 + 顶班选项",
    loopAround: {
      index: 3,
      label:
        "当首轮无法满足某条规则时，复核余额与政策、并以扩大的人选池重跑顶班搜索，而不是批准一份违反额度或让班次缺人的申请。",
    },
    humanStep: "主管审批（或驳回）并确认顶班",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "员工消息",
    inMessage:
      "老板我今天不舒服，去诊所拿了 1 天 MC，2 点的班来不了了 sorry 🙏 [附病假单照片]",
    outLabel: "智能体准备了什么",
    outLines: [
      "识别：1 天病假，今天，员工 Aina（12 号店，2–10 点班次）",
      "病假单已验证：从照片读出诊所名称、日期与 1 天时长；符合政策",
      "余额无碍：今年 14 天病假已用 3 天，病假免通知",
      "找到顶班：Faiz 与 Mei-Ling 均受过收银培训且今天有空；Faiz 已在 12 号店",
    ],
    outResult: "可审批：批准请假 + 一键将 Faiz 指派到 2 点班次",
    note: "主管一键审批 · 审批时更新排班与薪酬 · 每份申请 AI 成本约 RM 0.10–0.25。",
  },
  workflowDetail: [
    {
      title: "在员工所在之处接收申请",
      detail:
        "智能体在你员工真正使用的渠道上接收申请——一条 WhatsApp 消息、你的 HR 应用或聊天——读取它（包括马来语/英语/中文混合与语音转写），并回确认它所理解的：谁、哪种请假、哪些日期、哪个班次。",
    },
    {
      title: "对请假类型与紧急程度分类",
      detail:
        "它按类型路由申请——年假、病假、当天紧急，或无薪——因为各自有不同的政策路径、审批人与紧急程度。一次当天病假会直接跳到顶班搜索；一次计划中的年假则走通知期与封锁日期核查。",
    },
    {
      title: "核查政策、余额与病假单",
      detail:
        "对照员工的实时额度，它核查余额、通知期与封锁日期，对病假则以视觉读取证明——诊所、日期、时长——对照政策验证，并标记任何对不上的地方（倒填日期、篡改、不一致）。",
    },
    {
      title: "给出合格且有空的顶班建议",
      detail:
        "对受影响的班次，它在实时排班中搜索为该岗位受训/持证、有空、且在工时与加班上限内的员工——给出一小组合规的顶班选项，按契合度排序（同门店、轮换公平），或在无人合格时如实说明。",
    },
    {
      title: "组装审批摘要",
      detail:
        "它交给主管一份可直接操作的摘要：申请、政策结果、已验证的病假单字段与顶班选项——于是批准请假并指派顶班只需一键，而非一场表格作业。",
    },
    {
      title: "交给人处理",
      detail:
        "主管审批或驳回并确认顶班。正是这次审批写入请假记录与排班变更——没有任何内容被自动预订，任何违反政策或缺少顶班之处都会被呈现，绝不静默放行。",
    },
  ],
  howWeBuild:
    "它读取病假单与申请，但始终只是提议——是主管的审批把请假写入 HR 系统并指派顶班。它基于你的实时排班与额度数据工作，因此顶班建议是此刻真正合格且已排到空档的人，而不是陈旧名单上的名字，并且它每次都执行同样的政策——同样的封锁日期、通知规则与余额核查。病假单被视为敏感：只使用政策所需的字段（有效性、日期、时长），而非诊断，且它呈现的每个决定都能追溯到其来源的规则与排班记录。",
  agentLoop: [
    "申请 + 病假单 + 你的实时排班、余额与政策",
    "分类、验证并搜索顶班（MCP 工具）",
    "在把决定交给主管前复核政策与顶班",
  ],
  integrations: [
    "HRIS / 请假系统（额度、余额、请假记录）",
    "排班 / 劳动力管理（班次、技能、可用性）",
    "员工使用的渠道——WhatsApp、聊天或你的 HR 应用",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的路由前端按请假类型与紧急程度对每份申请分类，随后简短的分类型链路执行核查 → 验证病假单 → 给出顶班 → 摘要；每个工具通过进程内 MCP 服务器提供。",
    "上下文：员工的实时额度与余额、该请假类型的政策规则，以及实时排班（谁有技能且已排空档）按申请载入，因此核查与顶班建议反映当前状态，而非隔夜导出。",
    "工具：只读的额度/余额、政策与排班查询；面向病假单的视觉提取器；顶班匹配求解器（技能 + 可用性 + 劳动规则约束）；以及一个仅起草请假记录与拟议排班变更、供主管确认的写入工具——没有任何工具能自行预订请假或修改排班。",
    "防护机制与门槛：没有任何内容被自主批准——智能体提议，而主管的审批是写入请假与指派顶班的门槛；任何违反规则的（余额不足、通知期、无合格顶班）都作为标记呈现，绝不静默放行。",
    "病假单与敏感数据处理（PDPA/PDPC）：病假单被视为敏感——只提取并保留政策所需字段（有效性、日期、时长），诊断与自由文本不予存储，访问采用最小权限并留痕。",
    "不可信输入：申请文本与病假单图片被视为不可信（伪造的引用或精心构造的消息无法推动审批通过），真伪标记——诊所不符、日期被改——转交人处理。",
    "顶班匹配约束：求解器尊重技能与资质、可用性、最高工时与加班规则以及轮换公平，因此建议是合规且合理的，而非随便一个有空的人。",
    "可观测性与审计：每次分类、政策核查、所用病假单字段与顶班建议都会留痕并可追溯到规则与排班记录——一条用于 HR 及劳动法/PDPA 审视的审计轨迹。",
    "评估框架：一组真实历史申请的黄金集——干净、边缘（倒填病假单、零余额、封锁日期、无顶班可用）与对抗（伪造病假单）——配合确定性政策检查，并对模糊判断采用 LLM 作为评审，每次改动在上线前都做回归测试。",
  ],
  models: [
    {
      task: "跨渠道与语言理解申请",
      model: "Claude Sonnet",
      why: "可靠读取马来语/英语/中文混合的 WhatsApp 消息，抽取请假类型、日期与意图。",
    },
    {
      task: "读取并验证病假单（视觉）",
      model: "Claude Sonnet",
      why: "对拍照的诊所证明具备可靠视觉——诊所、日期、时长——即便是不佳的手机照片。",
    },
    {
      task: "高频分类与余额检查",
      model: "Claude Haiku",
      why: "对大量简单的请假类型与额度是/否检查最便宜、最快。",
    },
    {
      task: "处理顶班稀缺或政策冲突",
      model: "Claude Opus",
      why: "仅在顶班稀缺或政策冲突时调用——占申请的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每份申请",
        value: "约 RM 0.10–0.25",
        note: "消息 + 病假单视觉 + 政策核查 + 顶班搜索 + 摘要，按 Claude Sonnet/Haiku 的 token 费率",
      },
      {
        label: "AI 用量——每月 3,000 份申请",
        value: "约 RM 260–650 / 月",
        note: "对计划中（非当天）的请假用 Batch API 在非高峰处理可更低",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入你的 HRIS、排班系统与员工渠道约需数周；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比一个缺人的班次或一次加班救火，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于申请量、有多少是病假（需要视觉），以及顶班有多难找。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  modelChoice: {
    approach: "单一模型，按任务分级",
    rationale:
      "一份申请共享同一上下文——员工的额度、政策与排班——因此由一个完善配置的单一模型来做判断，用更便宜的 Haiku 处理大量简单检查，把 Opus 仅留给顶班稀缺或政策冲突的情形。可靠、每份成本更低、也更易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非一份请假申请及其顶班搜索。",
  },
  evaluation: [
    "先做确定性检查——额度、余额、通知期、封锁日期、病假单有效性",
    "自我验证——智能体在交接前复核政策并以更大人选池重跑顶班，宁可标记也不强行批准",
    "业务 KPI——每份申请的 HR 耗时、及时补上的班次比例、审批周转、政策例外率",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量你当前的流程——每份申请耗时、两名 HR 员工执行同一政策的一致程度，以及目前有多大比例的缺勤能及时补上。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "它是否达成了正确的请假与顶班结果？",
        metrics: [
          {
            name: "建议（对比资深 HR / 值班经理）",
            measures: "在留出的历史申请上，其请假决定与顶班建议与专家一致",
            target: "≥ 95%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "核查是否合理且完整？",
        metrics: [
          {
            name: "必需检查全部执行",
            measures: "每一项政策检查都执行——额度、余额、通知、封锁、病假单——不遗漏",
            target: "100%",
          },
          {
            name: "不提议违反政策的批准",
            measures: "它绝不建议批准一份违反额度、通知或劳动规则的申请",
            target: "零容忍",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "病假单字段提取准确率",
            measures: "是否正确读取证明的诊所、日期与时长？",
            target: "≥ 98%",
          },
          {
            name: "顶班建议有效性",
            measures: "所建议员工确实合格、有空且在工时/加班规则之内",
            target: "≥ 98%",
          },
          {
            name: "覆盖率",
            measures: "它为缺勤找到有效顶班的比例，对照人工基线衡量",
            target: "≥ 基线",
          },
          {
            name: "无敏感数据泄露",
            measures: "只保留政策所需的病假单字段；诊断与自由文本从不存储",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "智能体不会自行写入请假或排班变更——由主管审批。它仅在历史申请黄金集（含伪造病假单、零余额与无顶班案例）上超过人工基线并达到这些 GPA 目标后，才按门店逐一上线，且先以影子模式运行。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史请假与病假单申请上做概念验证——包括杂乱的 WhatsApp 申请与边缘案例——以影子模式运行，让你在任何内容被预订前先看到它的决定与顶班建议。聚焦单一门店的落地只需数周。",
  ctaTitle: "被病假单和请假申请淹没了？",
  ctaDescription:
    "告诉我们你的请假量、HR 系统与排班方式，我们会诚实告诉你请假与补班智能体是否值得构建，以及它能及时找到多少顶班。",
  faqs: [
    {
      question: "智能体会自行批准请假或修改排班吗？",
      answer:
        "不会。它负责读取、核查与提议——由主管审批，而这次审批才写入请假记录并指派顶班。它没有能力自行预订请假或修改排班；人在环路中是设计本身，而非事后补丁。",
    },
    {
      question: "它如何处理病假单与隐私？",
      answer:
        "它把病假单视为敏感：只提取政策所需字段——有效性、日期、时长——不存储诊断或自由文本，符合 PDPA/PDPC 且采用最小权限。看起来可疑的证明（诊所不符、日期被改）会被标记给人，而非自动接受。请将此视为你 HR 与合规团队的起点，而非法律意见。",
    },
    {
      question: "它如何寻找顶班？",
      answer:
        "它按技能与资质、可用性、最高工时与加班规则以及轮换公平，把缺口与你的实时排班匹配——给出几个按契合度排序的合规选项。若无人合格，它会如实说明，而不是建议一个不合适的人。",
    },
    {
      question: "它能配合 WhatsApp 与混合语言吗？",
      answer:
        "可以。它在员工本来就在的地方接收申请，理解马来语、英语与中文混合（以及语音消息），并在采取任何行动前始终确认它所理解的——谁、什么、哪些日期与班次。",
    },
    {
      question: "它能跨马来西亚和新加坡使用吗？",
      answer:
        "可以。它按各实体的请假政策与劳动规则配置——马来西亚与新加坡不同——并执行各门店的规则集。同一个智能体只要配上正确的政策集，就能同时服务两地。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一门店或业务单元的概念验证通常只需数周，先以影子模式运行；全面落地需要更长时间，主要是 HRIS 与排班集成以及让主管适应，而不是 AI 本身。",
    },
  ],
};

export const leaveManagement: AgentUseCase = {
  structure: {
    slug: "leave-management",
    status: "available",
    pattern: "routing",
    route: "solutions/agent-use-cases/leave-management",
    heroImage: "/images/agent-use-cases/leave-management-hero.jpg",
    cardImage: "/images/agent-use-cases/leave-management-card.jpg",
    relatedUseCases: ["hr-verification", "knowledge-agent"],
    trackLocation: "agent_use_case_leave_management",
    valueScore: 4,
    complexityScore: 4,
  },
  content: { en, ms, zh },
};
